import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DotPlotCell } from "./DotPlotCell";
import {
  CRITERIA_GROUPS,
  MOCK_EVALUATORS,
  compositeScore,
  type Candidate,
  type Criterion,
  type ScoreMap,
  type CriteriaCategory,
  type EvaluatorName,
} from "./mockSelectionData";

interface Props {
  candidate: Candidate | null;
  scores: ScoreMap;
  open: boolean;
  onClose: () => void;
}

function criterionVariance(vals: number[]): number {
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  return vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length;
}

const COLOR_LABEL: Record<CriteriaCategory["color"], string> = {
  blue:   "text-blue-700 bg-blue-50 border-blue-200",
  green:  "text-green-700 bg-green-50 border-green-200",
  purple: "text-purple-700 bg-purple-50 border-purple-200",
  amber:  "text-amber-700 bg-amber-50 border-amber-200",
};

export function CandidateDetailDialog({ candidate, scores, open, onClose }: Props) {
  if (!candidate) return null;

  const composite = compositeScore(candidate.id).toFixed(2);

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3" data-testid="candidate-dialog-title">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600">
              {candidate.name[0]}
            </div>
            <div>
              <span>{candidate.name}</span>
              <div className="flex gap-1.5 mt-0.5">
                <Badge variant="secondary" className="text-xs font-normal">{candidate.ageGroup}</Badge>
                <Badge variant="outline" className="text-xs font-normal">{candidate.position}</Badge>
              </div>
            </div>
            <span className="ml-auto text-2xl font-bold text-gray-900">{composite}<span className="text-sm font-normal text-gray-400">/5</span></span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Column header */}
          <div className="grid grid-cols-[1fr_60px_repeat(3,2rem)_2.5rem] gap-2 items-center px-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Criterion</span>
            <span className="text-xs font-semibold text-gray-400 text-center">Plot</span>
            {MOCK_EVALUATORS.map((ev: EvaluatorName) => (
              <span key={ev} className="text-xs font-semibold text-gray-400 text-center">{ev.replace("Selector ", "")}</span>
            ))}
            <span className="text-xs font-semibold text-gray-400 text-center">σ²</span>
          </div>

          {CRITERIA_GROUPS.map((group: CriteriaCategory, gi: number) => {
            const groupLabel = group.subcategory
              ? `${group.category} — ${group.subcategory}`
              : group.category;

            return (
              <div key={gi}>
                {/* Group header */}
                <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-xs font-semibold mb-2 ${COLOR_LABEL[group.color]}`}>
                  {groupLabel}
                </div>

                <div className="space-y-1">
                  {group.criteria.map((criterion: Criterion) => {
                    const vals = MOCK_EVALUATORS.map(
                      (ev: EvaluatorName) => scores[candidate.id][ev][criterion.key]
                    );
                    const v = criterionVariance(vals);
                    const highVariance = v > 1.0;

                    return (
                      <div
                        key={criterion.key}
                        className={`grid grid-cols-[1fr_60px_repeat(3,2rem)_2.5rem] gap-2 items-center px-1 py-1.5 rounded-lg ${highVariance ? "bg-amber-50" : "hover:bg-gray-50"}`}
                      >
                        <span className={`text-sm ${highVariance ? "font-medium text-amber-800" : "text-gray-700"}`}>
                          {criterion.label}
                          {highVariance && <span className="ml-1 text-amber-500">⚠</span>}
                        </span>
                        <DotPlotCell scores={vals} color={group.color} />
                        {vals.map((val: number, i: number) => (
                          <span
                            key={i}
                            className={`text-center text-sm font-mono ${highVariance ? "font-bold text-amber-700" : "text-gray-600"}`}
                          >
                            {val}
                          </span>
                        ))}
                        <span className={`text-center text-xs font-mono ${v > 1.5 ? "text-red-500 font-bold" : v > 0.5 ? "text-amber-500" : "text-gray-300"}`}>
                          {v.toFixed(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
